import { useEffect, useState } from "react";
import {
  CheckCircle,
  Mail,
  Clock2,
  ShieldCheck,
  Wallet,
  IdCard,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useGetConnectedAccount } from "../../../hooks/api/Tutors/Stripe/useGetConnectedAccount";
import { UploadOutlined } from "@ant-design/icons";
import { Button, Image, message, Modal, Upload } from "antd";
import axios from "axios";
import { getToken } from "../../../utils/getToken";
import { useConfirmVerification } from "../../../hooks/api/Tutors/Verification/useConfirmVerification";
import { queryClient } from "../../../services/queryClient";

const VerificationProgress = ({ tutor }: { tutor: any }) => {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const hasAvailability =
      Array.isArray(tutor?.data?.weeklyAvailability) &&
      tutor.data?.weeklyAvailability.some(
        (day: any) => Array.isArray(day.timeSlots) && day.timeSlots.length > 0
      );
    if (
      tutor?.data?.isEmailVerified &&
      hasAvailability &&
      tutor?.data?.isAccountVerified === true &&
      tutor?.data?.isStripeOnboarded === true
    )
      setCurrentStep(5);
    else if (
      tutor?.data?.isEmailVerified &&
      hasAvailability &&
      tutor?.data?.isAccountVerified === true
    ) {
      setCurrentStep(4);
    } else if (
      tutor?.data?.isEmailVerified &&
      hasAvailability &&
      tutor?.data?.isVerificationDocsUploaded === true
    ) {
      setCurrentStep(3);
    } else if (tutor?.data?.isEmailVerified && hasAvailability) {
      setCurrentStep(2);
    } else if (tutor?.data?.isEmailVerified) {
      setCurrentStep(1);
    }
  }, [tutor]);

  const [verify, setVerify] = useState(false);
  const [isOpening, setIsOpening] = useState(false);

  const [hasOpened, setHasOpened] = useState(false);

  const { isLoading } = useGetConnectedAccount({
    trigger: verify,
    hasOpened,
    setHasOpened,
  });

  const handleClick = () => {
    if (!isOpening) {
      setIsOpening(true);
      setVerify(true);

      setTimeout(() => {
        setIsOpening(false);
      }, 5000);
    }
  };

  const steps = [
    {
      title: "Complete Profile Setup",
      description: "Verify your email",
      icon: <Mail />,
    },
    {
      title: "Set Availability",
      description: "Set your availability for tutoring",
      icon: <Clock2 />,
    },
    {
      title: "Upload Valid ID",
      description: "Upload a photo of a government-issued ID",
      icon: <IdCard />,
    },
    {
      title: "Admin Verification",
      // description: "An admin personnel will verify your account",
      icon: <ShieldCheck />,
    },
    {
      title: "Payout Verification",
      description:
        "You'll be redirected to Stripe to complete the process and the linked account will receive booking payouts",
      icon: <Wallet />,
    },
  ];

  const token = getToken();
  const [payload, setPayload] = useState({
    verificationType: "ID",
    fileName: "",
    s3Url: "",
  });

  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleUpload = async (selectedFile: File) => {
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/misc/file-upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      message.success("File uploaded successfully!");

      setPayload((prev) => ({
        ...prev,
        fileName: selectedFile.name,
        s3Url: response.data.data.fileUrl,
      }));

      console.log(response.data);
    } catch (error) {
      message.error("File upload failed!");
      console.error(error);
    }
  };

  const handleFileChange = async (e: any) => {
    const selectedFile = e.target.files?.[0] || null;
    if (selectedFile) {
      await handleUpload(selectedFile);
    }
  };

  const { mutateAsync } = useConfirmVerification();

  const handleSubmit = () => {
    mutateAsync(payload, {
      onSuccess: () => {
        setIsModalVisible(false);
        queryClient.invalidateQueries("gettutor");
      },
    });
  };

  const progressWidth = `${(currentStep / 5) * 100}%`;

  return (
    <div className="p-5 bg-white rounded-md border border-primary500 mt-2">
      <h3 className="text-gray900 W2STMedium">
        Profile Completeness: {Math.round((currentStep / 5) * 100)}%
      </h3>
      <p className="text-gray500 mt-2">
        Complete the process to fully enjoy all the benefits of your tutor
        profile.
      </p>

      {/* Progress Bar */}
      <div className="bg-gray-200 rounded-md mt-5 h-2 w-full">
        <div
          className="bg-primary600 h-2 rounded-md transition-all"
          style={{ width: progressWidth }}
        ></div>
      </div>

      {/* Steps */}
      {steps.map((step, index) => (
        <div
          key={index}
          className={`flex items-center justify-between py-4 border-b ${
            index < currentStep ? "opacity-50 line-through" : ""
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`p-2 rounded-md ${
                index < currentStep
                  ? "bg-green-500 text-white"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {index < currentStep ? <CheckCircle size={20} /> : step.icon}
            </div>
            <div>
              <h3 className="W2STMedium text-gray-900">{step.title}</h3>
              <p className="text-sm text-gray-500">{step.description}</p>
              {index === 3 && (
                <div className="bg-primary100 rounded-[10px] text-[14px] p-2 max-w-[80%] mt-2">
                  Your are required to send an email of your background check to{" "}
                  <Link
                    to="/"
                    target="_blank"
                    className="text-primary700 W2STBold"
                  >
                    info@w2st.net
                  </Link>
                  . An admin personnel will verify your account once your
                  background check is verified
                </div>
              )}
            </div>
          </div>
          {index === 1 && (
            <Link
              to={index === currentStep ? "/tutor/availability" : "#"}
              onClick={(e) => {
                if (index !== currentStep) e.preventDefault();
              }}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                index === currentStep
                  ? "bg-black text-white"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed pointer-events-none"
              }`}
            >
              Start
            </Link>
          )}
          {index === 2 && (
            <>
              <button
                disabled={currentStep !== 2 || isLoading || isOpening}
                onClick={() => setIsModalVisible(true)}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  index === currentStep
                    ? "bg-black text-white"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed pointer-events-none"
                }`}
              >
                Start
              </button>
              <Modal
                width="400px"
                footer={null}
                closeIcon={false}
                open={isModalVisible}
                // onCancel={() => setVisibility(false)}
              >
                <div className="bg-white">
                  <h3 className="text-[20px] W2STMedium">Upload Valid ID</h3>
                  <p className="text-gray-600 mb-6">
                    To complete your verification, please upload a clear image
                    of a valid government-issued ID such as your passport,
                    national ID, or driver’s license.
                  </p>

                  {payload?.s3Url ? (
                    <div className="flex flex-col items-center gap-2">
                      <Image
                        src={payload.s3Url}
                        alt="document"
                        height={140}
                        style={{ borderRadius: "8px" }}
                      />
                    </div>
                  ) : (
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={(file) => {
                        handleFileChange({ target: { files: [file] } });
                        return false;
                      }}
                    >
                      <Button icon={<UploadOutlined />}>Upload Image</Button>
                    </Upload>
                  )}
                  <button
                    disabled={!payload.s3Url}
                    className={`mt-8 w-full py-3 bg-primary900 rounded-md text-white text-md float-right ${
                      !payload.s3Url ? "opacity-50" : ""
                    }`}
                    onClick={handleSubmit}
                  >
                    Submit
                  </button>
                  <div className="text-white">L</div>
                </div>
              </Modal>
            </>
          )}
          {index === 4 && (
            <button
              disabled={currentStep !== 4 || isLoading || isOpening}
              onClick={handleClick}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                index === currentStep
                  ? "bg-black text-white"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed pointer-events-none"
              }`}
            >
              Start
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default VerificationProgress;
